import MenuIcon from '@mui/icons-material/Menu';
import {Divider, IconButton, Menu, MenuItem} from '@mui/material';
import {toJpeg, toPng, toSvg} from 'html-to-image';
import {RefObject, useState} from 'react';

type Renderer = typeof toPng;

interface ChartExportMenuProps {
    target: RefObject<HTMLElement>;
    title: string;
}

// Mark controls with this attribute to keep them out of the exported image.
export const EXPORT_IGNORE = {'data-export-ignore': true};

const OPTIONS = {
    backgroundColor: '#fff',
    pixelRatio: 2,
    filter: (node: HTMLElement) => !node.dataset || node.dataset.exportIgnore === undefined,
};

const toFilename = (title: string) =>
    title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'grafico';

const download = (dataUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
};

const print = (dataUrl: string) => {
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0';
    document.body.appendChild(frame);

    const frameWindow = frame.contentWindow!;
    const image = frameWindow.document.createElement('img');
    image.style.maxWidth = '100%';
    image.onload = () => {
        frameWindow.addEventListener('afterprint', () => frame.remove());
        frameWindow.focus();
        frameWindow.print();
    };
    image.src = dataUrl;
    frameWindow.document.body.appendChild(image);
};

const ChartExportMenu = ({target, title}: ChartExportMenuProps) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const render = (renderer: Renderer) => {
        setAnchor(null);

        return target.current ? renderer(target.current, OPTIONS) : Promise.reject();
    };

    const exportAs = (renderer: Renderer, extension: string) => () =>
        render(renderer)
            .then((dataUrl) => download(dataUrl, `${toFilename(title)}.${extension}`))
            .catch(() => undefined);

    const printChart = () =>
        render(toPng)
            .then(print)
            .catch(() => undefined);

    return (
        <>
            <IconButton
                {...EXPORT_IGNORE}
                size='small'
                aria-label='Esporta grafico'
                onClick={(event) => setAnchor(event.currentTarget)}
                sx={{p: '2px', color: 'text.disabled', flexShrink: 0}}
            >
                <MenuIcon sx={{fontSize: 18}} />
            </IconButton>
            <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
                <MenuItem dense onClick={printChart}>Print chart</MenuItem>
                <Divider />
                <MenuItem dense onClick={exportAs(toPng, 'png')}>Download PNG image</MenuItem>
                <MenuItem dense onClick={exportAs(toJpeg, 'jpeg')}>Download JPEG image</MenuItem>
                <MenuItem dense onClick={exportAs(toSvg, 'svg')}>Download SVG vector image</MenuItem>
            </Menu>
        </>
    );
};

export default ChartExportMenu;
